import { forwardRef, useImperativeHandle, useState, useCallback, useMemo, useEffect } from "react";
import "./GenerateSubArea.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";

// Constants outside component to prevent recreation
const NUMBER_REGEX = /^\d*$/;

// Field configuration for better maintainability
const FORM_FIELDS_CONFIG = [
  {
    id: "rows",
    label: "Rows",
    name: "rows",
    type: "text",
    required: true,
    validation: (value) => value > 0,
    errorMessage: "Rows must be greater than 0",
  },
  {
    id: "columns",
    label: "Columns",
    name: "columns",
    type: "text",
    required: true,
    validation: (value) => value > 0,
    errorMessage: "Columns must be greater than 0",
  },
  {
    id: "maxCapacity",
    label: "Max Capacity",
    name: "maxCapacity",
    type: "number",
    required: false,
    info: {
      show: true,
      text: "This is considered only when there is a value",
      icon: faInfoCircle,
    },
    validation: (value) => !value || value > 0,
    errorMessage: "Max capacity must be greater than 0 if provided",
  },
];

// Custom InfoIcon component for better reusability
const InfoIcon = ({ text, icon = faInfoCircle, position = "top", className = "" }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className={`info-icon-wrapper ${className}`}>
      <span
        className="info-icon"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsHovered(true)}
        onBlur={() => setIsHovered(false)}
        role="tooltip"
        aria-label={`Info: ${text}`}
        tabIndex={0}
      >
        <FontAwesomeIcon icon={icon} />
      </span>
      {isHovered && <div className={`info-tooltip info-tooltip-${position}`}>{text}</div>}
    </div>
  );
};

const GenerateSubArea = forwardRef((props, ref) => {
  // Initial state with proper default values
  const initialState = {
    rows: null,
    columns: null,
    maxCapacity: props.initialMaxCapacity || null,
    touched: {},
    errors: {},
    showFormError: false,
  };

  const [formState, setFormState] = useState(initialState);

  // Sync with external maxCapacity changes
  useEffect(() => {
    if (props.initialMaxCapacity !== undefined) {
      setFormState((prev) => ({
        ...prev,
        maxCapacity: props.initialMaxCapacity,
      }));
    }
  }, [props.initialMaxCapacity]);

  // Validate a single field
  const validateField = useCallback((name, value, isRequired) => {
    // For required fields, check if value exists and is valid
    if (isRequired && (value === null || value === "" || value === undefined)) {
      return "This field is required";
    }

    // If field is not required and empty, it's valid
    if (!isRequired && (value === null || value === "" || value === undefined)) {
      return "";
    }

    // Convert to number for validation
    const numericValue = Number(value);

    const fieldConfig = FORM_FIELDS_CONFIG.find((field) => field.name === name);
    if (!fieldConfig || !fieldConfig.validation) return "";

    return fieldConfig.validation(numericValue) ? "" : fieldConfig.errorMessage;
  }, []);

  // Validate all required fields are filled
  const validateRequiredFields = useCallback((formData) => {
    const requiredFields = FORM_FIELDS_CONFIG.filter((field) => field.required);
    return requiredFields.every((field) => {
      const value = formData[field.name];
      return value !== null && value !== "" && value !== undefined;
    });
  }, []);

  // Handle input change with validation
  const handleInputChange = useCallback(
    (event) => {
      const { name, value } = event.target;

      // Accept only numbers or empty string
      if (!NUMBER_REGEX.test(value)) {
        return;
      }

      const fieldConfig = FORM_FIELDS_CONFIG.find((field) => field.name === name);
      const isRequired = fieldConfig?.required || false;

      const numericValue = value === "" ? null : Number(value);
      const error = validateField(name, numericValue, isRequired);

      setFormState((prev) => ({
        ...prev,
        [name]: numericValue,
        touched: { ...prev.touched, [name]: true },
        errors: { ...prev.errors, [name]: error },
        showFormError: false, // Reset form error when user starts typing
      }));
    },
    [validateField],
  );

  // Handle blur for validation
  const handleInputBlur = useCallback(
    (event) => {
      const { name, value } = event.target;

      const fieldConfig = FORM_FIELDS_CONFIG.find((field) => field.name === name);
      const isRequired = fieldConfig?.required || false;

      const numericValue = value === "" ? null : Number(value);
      const error = validateField(name, numericValue, isRequired);

      setFormState((prev) => ({
        ...prev,
        touched: { ...prev.touched, [name]: true },
        errors: { ...prev.errors, [name]: error },
      }));
    },
    [validateField],
  );

  // Memoize form data for useImperativeHandle
  const formData = useMemo(
    () => ({
      rows: formState.rows,
      columns: formState.columns,
      maxCapacity: formState.maxCapacity,
    }),
    [formState.rows, formState.columns, formState.maxCapacity],
  );

  // Check if required fields are filled
  const areRequiredFieldsFilled = useMemo(() => {
    return validateRequiredFields(formState);
  }, [formState, validateRequiredFields]);

  // Check if form is valid (no errors)
  const isFormValid = useMemo(() => {
    return Object.values(formState.errors).every((error) => !error);
  }, [formState.errors]);

  // Check if generate button should be enabled
  const isGenerateEnabled = useMemo(() => {
    return areRequiredFieldsFilled && isFormValid;
  }, [areRequiredFieldsFilled, isFormValid]);

  // Memoize processed fields for rendering
  const processedFields = useMemo(
    () =>
      FORM_FIELDS_CONFIG.map((field) => ({
        ...field,
        value: formState[field.name] ?? "",
        error: formState.errors[field.name],
        isTouched: formState.touched[field.name],
      })),
    [formState],
  );

  // Handle generate button click
  const handleGenerate = useCallback(
    (e) => {
      e.preventDefault();

      // Validate all fields on submit
      const newErrors = {};
      let hasErrors = false;

      FORM_FIELDS_CONFIG.forEach((field) => {
        const value = formState[field.name];
        const error = validateField(field.name, value, field.required);
        if (error) {
          newErrors[field.name] = error;
          hasErrors = true;
        }
      });

      // Check if required fields are filled
      const requiredFieldsFilled = validateRequiredFields(formState);

      if (hasErrors || !requiredFieldsFilled) {
        // Mark all required fields as touched to show errors
        const newTouched = {};
        FORM_FIELDS_CONFIG.forEach((field) => {
          if (field.required) {
            newTouched[field.name] = true;
          }
        });

        setFormState((prev) => ({
          ...prev,
          errors: { ...prev.errors, ...newErrors },
          touched: { ...prev.touched, ...newTouched },
          showFormError: !requiredFieldsFilled,
        }));

        return;
      }

      // Call the generate handler if provided
    },
    [formState, formData, validateField, validateRequiredFields],
  );

  // Expose methods and data through ref
  useImperativeHandle(
    ref,
    () => ({
      getFormData: () => formData,
      isValid: () => isFormValid,
      areRequiredFieldsFilled: () => areRequiredFieldsFilled,
      getErrors: () => formState.errors,
      resetForm: () => setFormState(initialState),
      formState: () => formState,
    }),
    [formData, isFormValid, areRequiredFieldsFilled, formState.errors, initialState],
  );

  return (
    <div className="generate-sub-area">
      <form className="generate-sub-area-form" onSubmit={handleGenerate} noValidate>
        {processedFields.map((field) => (
          <div key={field.id} className="form-group">
            <div className="form-label-wrapper">
              <label htmlFor={field.id}>
                {field.label}
                {field.required && <span className="required-star">*</span>}
                {field.isTouched && field.error && (
                  <span className="error-message" role="alert">
                    {field.error}
                  </span>
                )}
              </label>

              {field.info?.show && <InfoIcon text={field.info.text} icon={field.info.icon} className="field-info-icon" />}
            </div>

            <input
              id={field.id}
              name={field.name}
              type={field.type}
              value={field.value}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              aria-label={field.label}
              aria-required={field.required}
              aria-invalid={!!field.error}
              aria-describedby={field.error ? `${field.id}-error` : undefined}
              inputMode="numeric"
              pattern="[0-9]*"
              className={field.error ? "input-error" : ""}
              placeholder={field.required ? "Required" : "Optional"}
            />

            {field.error && (
              <span id={`${field.id}-error`} className="visually-hidden">
                Error: {field.error}
              </span>
            )}
          </div>
        ))}

        {/* Form-level error message */}
        {formState.showFormError && (
          <div className="form-error-message" role="alert">
            ⚠️ Please fill all required fields
          </div>
        )}
      </form>
    </div>
  );
});

// Set display name for better debugging
GenerateSubArea.displayName = "GenerateSubArea";

export default GenerateSubArea;
